import React, { useEffect, useState } from 'react';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import CommanTasks from './CommanTasks';
import Loader from '../../shared/components/ui/Loader';
import FormInput from '../../shared/components/ui/FormInput';
import Button from '../../shared/components/ui/Button';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { setTemplateActiveTab } from '../../../uiSlice';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useForm } from 'react-hook-form';
import {
  getTaskTemplates,
  createTaskOtherTemplate,
  getAllTaskTemplates,
  checkDuplicateTaskTemplateName
} from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import IndependentTasks from './IndependentTasks';
import GroupTasks from './GroupTasks';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { capitalizeWords } from '../utils/stringUtils';

const TaskTemplates = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const dispatch = useDispatch();
  const activeTab = useSelector((state) => state.ui.templateActiveTab);
  const [isloading, setisLoading] = React.useState(false);
  const { collapsed } = useSidebar();
  const [triggerBulkCreate, setTriggerBulkCreate] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Form for template creation
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  const selectedProject = watch('project');
  const templateName = watch('templateName');

  const { data: TaskTemplates, refetch: refetchTaskTemplates } = useQuery({
    queryKey: ['getTaskTemplates', selectedProject, activeTab],
    queryFn: () => getTaskTemplates(selectedProject, activeTab),
    enabled: !!selectedProject && !!activeTab
  });

  const { data: AllTaskTemplates, refetch: refetchAllTaskTemplates } = useQuery({
    queryKey: ['getAllTaskTemplates', activeTab],
    queryFn: () => getAllTaskTemplates(activeTab),
    enabled: !!activeTab
  });

  useEffect(() => {
    if (projects) {
      setValue('project', projects?.[0]?.id);
    }
  }, [projects]);

  // Reset template dropdown when tab or project changes and select first template
  useEffect(() => {
    if (TaskTemplates && TaskTemplates.length > 0) {
      setValue('template', TaskTemplates[0].name);
    } else {
      setValue('template', '');
    }
  }, [activeTab, selectedProject, TaskTemplates]);

  // Update active tab in redux
  const handleTabChange = (tab) => {
    dispatch(setTemplateActiveTab(tab));
  };

  // Handle template creation
  const handleCreateTemplate = async (data) => {
    if (!data.project) {
      toast.error('Please select a project');
      return;
    }
    if (!data.templateName) {
      toast.error('Please enter template name');
      return;
    }
    if (!data.template) {
      toast.error('Please select a base template');
      return;
    }

    const response = await checkDuplicateTaskTemplateName({
      project_id: data.project,
      name: data.templateName
    });

    if (response?.exist) {
      toast.error('Template name already exists');
      return;
    }

    // If user selected "Create from scratch", open bulk create modal instead of calling API
    if (data.template === 'scratch') {
      mixpanel.track('Create From Scratch Selected', {
        user_id: userId,
        project_id: data.project,
        template_name: data.templateName,
        type: activeTab,
        timestamp: new Date().toISOString()
      });
      setTriggerBulkCreate(true);
      // Reset trigger after a short delay to allow re-triggering
      setTimeout(() => setTriggerBulkCreate(false), 100);
      return;
    }

    const payload = {
      newProject_id: data.project,
      newName: capitalizeWords(data.templateName),
      type: activeTab,
      name: data.template,
      project_id: AllTaskTemplates?.find((template) => template.name === data.template)?.project?.id
    };

    setIsSubmitting(true);
    // Call API to create template
    try {
      const response = await createTaskOtherTemplate(payload);
      console.log('Template creation response:', response);
      toast.success('Template created successfully');
      setValue('templateName', '');
      // Reset to first template after creation
      if (TaskTemplates && TaskTemplates.length > 0) {
        setValue('template', TaskTemplates[0].id);
      }
      await refetchTaskTemplates();
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error creating template:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error creating template');
    }
  };

  const TransformedTaskTemplates = AllTaskTemplates?.map((template, index) => ({
    label: template.name + ' (' + template?.project?.name + ')',
    value: template.name,
    id: template.id,
    selected: index === 0
  }));

  return (
    <Layout>
      {/* Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header backArrow={false} data={{ name: 'TaskTemplates' }} />
      </div>

      <Loader isVisible={isloading || isProjectsLoading} />

      {/* Tabs */}
      <div
        className={`fixed top-[4.1rem] z-30 bg-white px-6 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <div className="flex gap-8 relative py-3 pb-4">
          <button
            className={`text-[1rem] font-semibold transition-colors duration-200 relative ${
              activeTab === 'common' ? 'text-[#2A565E]' : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={() => handleTabChange('common')}>
            Common Tasks
            {activeTab === 'common' && (
              <div className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#2A565E] transition-all duration-300" />
            )}
          </button>
          <button
            className={`text-[1rem] font-semibold transition-colors duration-200 relative ${
              activeTab === 'independent' ? 'text-[#2A565E]' : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={() => handleTabChange('independent')}>
            Independent Tasks
            {activeTab === 'independent' && (
              <div className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#2A565E] transition-all duration-300" />
            )}
          </button>
          <button
            className={`text-[1rem] font-semibold transition-colors duration-200 relative ${
              activeTab === 'group' ? 'text-[#2A565E]' : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={() => handleTabChange('group')}>
            Group Tasks
            {activeTab === 'group' && (
              <div className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#2A565E] transition-all duration-300" />
            )}
          </button>
        </div>
      </div>

      <div
        className={`fixed top-[7.35rem] border-t  z-20 bg-white px-6 pt-2 pb-4 border-gray-200 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <form
          onSubmit={handleSubmit(handleCreateTemplate)}
          className="flex items-end justify-between w-full">
          <div className="flex items-center gap-4 w-full">
            <FormInput
              width={20}
              label=""
              register={register}
              register_key="project"
              isRequired={true}
              type="dropdown"
              options={[
                { label: 'Select Project', value: '', disabled: true, selected: true },
                ...projectsForDropdown
              ]}
              validation={{ required: 'Project is required' }}
              errors={errors}
              hideAstrik={true}
            />
            <FormInput
              width={20}
              label=""
              register={register}
              register_key="templateName"
              isRequired={true}
              type="text"
              placeholder="Enter template name"
              validation={{ required: 'Template name is required' }}
              errors={errors}
              hideAstrik={true}
            />
            <FormInput
              width={20}
              label=""
              register={register}
              register_key="template"
              isRequired={true}
              type="dropdown"
              options={[
                ...(TransformedTaskTemplates || []),
                { label: 'Create from scratch', value: 'scratch' }
              ]}
              validation={{ required: 'Base template is required' }}
              errors={errors}
              hideAstrik={true}
            />
          </div>
          <Button
            type="submit"
            customStyle={{ whiteSpace: 'nowrap' }}
            disabled={isSubmitting || isProjectsLoading}>
            {isSubmitting ? 'Creating Template...' : 'Create Template'}
          </Button>
        </form>
      </div>

      {/* Grid - adjusted margin top to account for template management section */}
      <div
        style={{
          marginTop: activeTab === 'independent' || activeTab === 'group' ? '12rem' : '8rem'
        }}>
        <GridWrapper width={100}>
          {activeTab === 'common' ? (
            <CommanTasks
              setisLoading={setisLoading}
              TaskTemplates={TaskTemplates || []}
              projectId={selectedProject}
              triggerBulkCreate={triggerBulkCreate}
              bulkCreateTemplateName={templateName}
              refreshtemplates={() => {
                refetchTaskTemplates();
                setValue('template', TaskTemplates[0].name);
                setValue('templateName', '');
              }}
            />
          ) : activeTab === 'independent' ? (
            <IndependentTasks
              setisLoading={setisLoading}
              TaskTemplates={TaskTemplates || []}
              projectId={selectedProject}
              triggerBulkCreate={triggerBulkCreate}
              bulkCreateTemplateName={templateName}
              refreshtemplates={() => {
                refetchTaskTemplates();
                setValue('template', TaskTemplates[0].name);
                setValue('templateName', '');
              }}
            />
          ) : activeTab === 'group' ? (
            <GroupTasks
              setisLoading={setisLoading}
              TaskTemplates={TaskTemplates || []}
              projectId={selectedProject}
              triggerBulkCreate={triggerBulkCreate}
              bulkCreateTemplateName={templateName}
              refreshtemplates={() => {
                refetchTaskTemplates();
                setValue('template', TaskTemplates[0].name);
                setValue('templateName', '');
              }}
            />
          ) : null}
        </GridWrapper>
      </div>
    </Layout>
  );
};

export default TaskTemplates;
